import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from 'react-beautiful-dnd';
import styled from 'styled-components';
import * as Yup from 'yup';

import {
  Avatar,
  Box,
  Button,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemProps,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  InsertDriveFile as InsertDriveFileIcon
} from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import { calculateReorderIndex } from '../../../common/calculateReorderIndex';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineForm from '../../../common/inline/InlineForm';
import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import { formatImgURLs } from '../../../common/inline/InlineHtml.hooks';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineTextField from '../../../common/inline/InlineTextField';
import { reorder } from '../../../common/reorder.helper';
import { ApprovedFileVersion } from '../../documents/Document.interface';
import DocumentAutoComplete from '../../documents/components/DocumentAutocomplete';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useUpdateTraining } from '../Training.hooks';
import { BaseTrainingCardProps, TrainingSection } from '../Training.interface';
import {
  TrainingUserFileUpload,
  trainingUserFileUploadTypes
} from '../components/TrainingUserFileUpload';

const StyledGrid = styled(Grid)`
  flex: 1;
  .grid-flex,
  .card-flex,
  .card-content-flex {
    flex: 1;
    display: flex;
    flex-direction: column;

    &.hidden {
      display: none;
    }

    .tox-tinymce {
      flex: 1;
      min-height: 512px;
    }
  }
  .card-flex,
  .card-content-flex {
    border-radius: 4px 4px 0 0;
  }
  .file-upload-grid {
    .file-upload-container {
      background: ${props => props.theme.palette.grey[100]};
      padding: 3px;
      border-radius: 4px;
    }
  }

  ${props => props.theme.breakpoints.down('sm')} {
    flex-direction: column;
  }
`;

const StyledList = styled(List)`
  padding: 0;
  background-color: ${props => props.theme.palette.grey[100]};
  .MuiAvatar-root {
    background-color: ${props => props.theme.palette.primary.main};
  }
`;

const StyledListItem = styled(ListItem)`
  background: #fff;
  border-bottom: solid 1px #ccc;
` as React.ComponentType<ListItemProps>;

const StyledDocumentReferenceDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0 0;
  .label {
    padding: 0 10px;
  }
  .document-autocomplete {
    flex: 1;
  }
`;

function getTrainingSections(
  sections: TrainingSection[] | undefined
): TrainingSection[] {
  return sections && sections.length > 0
    ? sections
    : [{ name: 'Untitled', body: '' }];
}

const fileUploadSchema = Yup.object().shape({
  minRequired: Yup.number().min(
    0,
    'Invalid minimum required file upload count.'
  )
});

const TrainingTabEdit: React.FC<BaseTrainingCardProps> = (
  props: BaseTrainingCardProps
) => {
  const { training, onChange, onUpdate, onError } = props;
  const [sections, setSections] = useState(() => {
    return getTrainingSections(training.sections);
  });
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [didReorder, setDidReorder] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const section = sections[selectedSectionIndex] as TrainingSection | undefined;
  const {
    training: updatedTraining,
    isLoading: isUpdating,
    error: updateError,
    patch
  } = useUpdateTraining(training.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(training.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Training>(
    training.task
  );
  const isEditable: boolean = !isFieldDisabled('sections');
  const deleteSectionName: string =
    sections[deleteIndex]?.name || `Section #${deleteIndex + 1}`;
  const error: FetchError | undefined = updateError || updateTaskError;

  // get file metadata of current section
  const fileMetadata = training.fileMetadata?.find(file => {
    return (
      section &&
      file.id === section.fileId &&
      file.fileVersion === section.fileVersion &&
      file.storageKey === section.url
    );
  });

  const removeSection = (index: number) => {
    // select first section if deleting current section
    if (selectedSectionIndex === index) {
      setSelectedSectionIndex(0);
    }
    // delete the section
    const newSections: TrainingSection[] = [...sections];
    newSections.splice(index, 1);
    // insert default section
    if (newSections.length === 0) {
      newSections.push({ name: 'Untitled', body: '' });
    }
    setSections(newSections);
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    const reorderedSections = reorder(
      sections,
      source.index,
      destination.index
    );
    // set index to selected section if it was moved
    const index: number = calculateReorderIndex(
      selectedSectionIndex,
      source.index,
      destination.index
    );
    setSelectedSectionIndex(index);
    // maintain focus to selected section
    inputsRef.current[index]?.focus?.();
    setDidReorder(true);
    setSections(reorderedSections);
  };

  const handleSave = () => {
    // only strip tokens from img src during saving
    // since removing them onSubmit causes the field to re-render
    const newSections = sections.map(section => {
      const copy: TrainingSection = { ...section };
      copy.body = formatImgURLs(section.body, true);
      return copy;
    });

    setSections(newSections);
    patch({ sections: newSections });
    onChange?.({ sections: newSections });
  };

  // only set update on change training.sections
  useEffect(() => {
    setSections(getTrainingSections(training.sections));
  }, [training.sections]);

  // save when re-ordered
  useEffect(() => {
    if (didReorder) {
      setDidReorder(false);
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  useEffect(() => {
    if (updatedTraining) {
      onChange?.(updatedTraining);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTraining]);

  useEffect(() => {
    if (updatedTask) {
      training.task = Object.assign(training.task || {}, updatedTask);
      onChange?.(training);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const getCardContentClassName = useCallback(
    (index: number) => {
      const classes = ['card-content-flex'];
      if (index !== tabIndex) {
        classes.push('hidden');
      }
      return classes.join(' ');
    },
    [tabIndex]
  );

  return (
    <StyledGrid container spacing={2}>
      <Grid item xs={12} md={3} className='grid-flex'>
        <Paper className='card-flex'>
          <CardTitle>Training Sections</CardTitle>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='terms'>
              {(provided, _snapshot) => (
                <List ref={provided.innerRef}>
                  <Divider component='li' />

                  {sections.map((section, index) => (
                    <Draggable
                      key={index}
                      index={index}
                      isDragDisabled={!isEditable}
                      draggableId={`${section.name}-${index}`}
                    >
                      {(provided, _snapshot) => (
                        <StyledListItem
                          onClick={() => setSelectedSectionIndex(index)}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <ListItemIcon>
                            <DragIndicatorIcon />
                          </ListItemIcon>
                          <InputBase
                            style={{ width: '100%' }}
                            value={section.name}
                            disabled={!isEditable}
                            inputRef={ref => (inputsRef.current[index] = ref)}
                            onChange={event => {
                              const copy = [...sections];
                              copy[index].name = event.target.value;
                              setSections(copy);
                            }}
                            endAdornment={
                              <IconButton
                                onClick={() => {
                                  setOpenDialog(true);
                                  setDeleteIndex(index);
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            }
                          />
                        </StyledListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {isEditable && (
                    <ListItem
                      button
                      onClick={() => {
                        const copy = [...sections];
                        copy.push({
                          name: 'Untitled',
                          body: ''
                        });
                        setSections(copy);
                      }}
                    >
                      <ListItemIcon>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText primary='Add Section' />
                    </ListItem>
                  )}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </Paper>
      </Grid>
      <Grid item xs={12} md={9} className='grid-flex'>
        <Paper className='card-flex'>
          <Tabs
            variant='scrollable'
            indicatorColor='primary'
            scrollButtons='auto'
            value={tabIndex}
            onChange={(_, index) => setTabIndex(index)}
          >
            <Tab label='Content' />
            <Tab label='File Upload' />
          </Tabs>
          <CardContent className={getCardContentClassName(0)}>
            <InlineFormik
              error={updateTaskError}
              isSubmitting={isUpdating}
              initialValues={{ body: section?.body || '' }}
              onSubmit={({ body }) => {
                const copy: TrainingSection[] = [...sections];
                copy[selectedSectionIndex].body = body;
                setSections(copy);
              }}
            >
              <InlineHtml
                name='body'
                height='100%'
                enableMedia
                hideConfirm
                submitOnChange
                disabled={!isEditable || isUpdating}
                attachments={training.task.attachments || []}
                onChangeAttachments={(attachments: InlineFileValue[]) => {
                  updateTask({ attachments });
                  if (training.task) {
                    training.task.attachments = attachments;
                  }
                  // only update task since training.sections is outdated
                  onChange?.({ task: training.task });
                }}
              />
            </InlineFormik>
            <StyledDocumentReferenceDiv>
              <div className='label'>
                <Typography variant='overline'>Reference Document</Typography>
              </div>
              <DocumentAutoComplete
                className='document-autocomplete'
                value={{
                  id: section?.fileId,
                  version: section?.fileVersion,
                  url: section?.url
                }}
                disabled={!isEditable}
                onChange={(document?: ApprovedFileVersion) => {
                  console.log('onChange', document);
                  const copy = [...sections];
                  copy[selectedSectionIndex].url = document?.storageKey ?? '';
                  copy[selectedSectionIndex].fileId = document?.id;
                  copy[selectedSectionIndex].fileVersion = document?.version;
                  setSections(copy);
                }}
              />
            </StyledDocumentReferenceDiv>

            {fileMetadata && (
              <Box mt={1} boxShadow={1}>
                <StyledList dense>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <InsertDriveFileIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <>
                          <strong>{fileMetadata.taskKey}</strong>:{' '}
                          {fileMetadata.name} (v{fileMetadata.version})
                        </>
                      }
                      secondary='Last saved Document'
                    />
                  </ListItem>
                </StyledList>
              </Box>
            )}
          </CardContent>
          <CardContent
            key={selectedSectionIndex}
            className={getCardContentClassName(1)}
          >
            <Grid container spacing={2}>
              <Grid item md={6} sm={12} xs={12}>
                <InlineFormik
                  strict={false}
                  validationSchema={fileUploadSchema}
                  initialValues={{
                    enabled: section?.fileUpload?.enabled || false,
                    multiple: section?.fileUpload?.multiple || false,
                    minRequired: section?.fileUpload?.minRequired ?? 0,
                    label: section?.fileUpload?.label || '',
                    description: section?.fileUpload?.description || '',
                    fileType: section?.fileUpload?.fileType || ''
                  }}
                  onSubmit={values => {
                    setSections(sections => {
                      const copy = [...sections];
                      const section = { ...copy[selectedSectionIndex] };
                      section.fileUpload = { ...section.fileUpload, ...values };
                      copy[selectedSectionIndex] = section;
                      return copy;
                    });
                  }}
                >
                  {({ setFieldValue, submitForm, values }) => {
                    const disabled = !values.enabled;
                    const minRequired = values.minRequired;
                    const withMinRequired =
                      typeof minRequired === 'number' && minRequired > 0;
                    return (
                      <InlineForm>
                        <InlineFormControl>
                          <FormControlLabel
                            label='Enable File Upload'
                            control={
                              <Checkbox
                                size='small'
                                color='primary'
                                checked={values.enabled}
                                onChange={(_, checked) => {
                                  setFieldValue('enabled', checked, true);
                                  submitForm();
                                }}
                              />
                            }
                          />
                        </InlineFormControl>
                        <InlineFormControl style={{ display: 'block' }}>
                          <FormControlLabel
                            label='Multiple Files'
                            disabled={disabled}
                            control={
                              <Checkbox
                                size='small'
                                color='primary'
                                checked={values.multiple}
                                onChange={(_, checked) => {
                                  setFieldValue('multiple', checked, true);
                                  if (!checked) {
                                    // only 1 min required if single upload
                                    setFieldValue(
                                      'minRequired',
                                      withMinRequired ? 1 : 0,
                                      true
                                    );
                                  }
                                  submitForm();
                                }}
                              />
                            }
                          />
                        </InlineFormControl>
                        {values.multiple ? (
                          <InlineTextField
                            disabled={disabled}
                            type='number'
                            label='Minimum Required File Uploads'
                            name='minRequired'
                            helperText={
                              !minRequired
                                ? 'No minimum required file uploads.'
                                : `Minimum number of required file uploads: ${minRequired}.`
                            }
                          />
                        ) : (
                          // show checkbox for toggling the required of single upload
                          <InlineFormControl style={{ display: 'block' }}>
                            <FormControlLabel
                              label='Required'
                              disabled={disabled}
                              control={
                                <Checkbox
                                  size='small'
                                  color='primary'
                                  checked={withMinRequired}
                                  onChange={(_, checked) => {
                                    setFieldValue(
                                      'minRequired',
                                      checked ? 1 : 0,
                                      true
                                    );
                                    submitForm();
                                  }}
                                />
                              }
                            />
                          </InlineFormControl>
                        )}
                        <InlineTextField
                          disabled={disabled}
                          name='label'
                          label='Label'
                        />
                        <InlineTextField
                          disabled={disabled}
                          name='description'
                          label='Description'
                        />
                        <InlineSelect
                          name='fileType'
                          disabled={disabled}
                          label='File Type'
                          options={trainingUserFileUploadTypes}
                        />
                      </InlineForm>
                    );
                  }}
                </InlineFormik>
              </Grid>
              <Grid className='file-upload-grid' item md={6} sm={12} xs={12}>
                <Typography variant='overline'>Preview:</Typography>
                <TrainingUserFileUpload
                  disabled
                  label={section?.fileUpload?.label}
                  description={section?.fileUpload?.description}
                  progress={
                    section?.fileUpload &&
                    typeof section?.fileUpload.minRequired === 'number'
                      ? {
                          index: 0,
                          total: 0,
                          min: section?.fileUpload.minRequired,
                          isValid: false
                        }
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Box
              flex={1}
              display='flex'
              alignItems='center'
              justifyContent='flex-end'
              mr={1}
            >
              {!isEditable && <Box mr={1}>Start a new version to edit</Box>}
              {isUpdating && (
                <Box mr={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
              <Button
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={isUpdating || !isEditable}
              >
                Save
              </Button>
            </Box>
          </CardActions>
        </Paper>
      </Grid>

      <ConfirmDialog
        open={openDialog && deleteIndex > -1}
        confirmLabel='Confirm'
        title='Remove Training Section'
        message={`Are you sure you want to remove this (${deleteSectionName}) Training section?`}
        onCancel={() => {
          setOpenDialog(false);
          setDeleteIndex(-1);
        }}
        onConfirm={() => {
          setOpenDialog(false);
          if (deleteIndex > -1) {
            removeSection(deleteIndex);
            setDeleteIndex(-1);
          }
        }}
      />
    </StyledGrid>
  );
};

export default TrainingTabEdit;
